/**
 * Customer Onboarding API client.
 *
 * All endpoints are served at /api/platform/v1/support/onboarding/* and
 * backed by the onboarding_router on the backend.
 */
import { apiClient } from '@/services/apiClient';

const BASE = '/api/platform/v1/support/onboarding';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface OnboardingTemplate {
  id: number;
  cbo_id: number | null;
  name: string;
  description: string | null;
  customer_type: string | null;
  is_active: boolean;
  is_platform_default: boolean;
  version: number;
  cloned_from_id: number | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  phase_count?: number;
}

export interface OnboardingPhase {
  id: number;
  template_id: number;
  name: string;
  description: string | null;
  sort_order: number;
  target_duration_days: number | null;
  created_at: string;
}

export interface OnboardingMilestone {
  id: number;
  phase_id: number;
  name: string;
  description: string | null;
  sort_order: number;
  exit_criteria: string | null;
  created_at: string;
}

export interface OnboardingTaskTemplate {
  id: number;
  milestone_id: number;
  name: string;
  description: string | null;
  sort_order: number;
  default_owner_role: string | null;
  estimated_hours: number | null;
  action_type: string;
  action_target: string | null;
  action_payload: Record<string, unknown> | null;
  required: boolean;
  depends_on_task_id: number | null;
  created_at: string;
}

export interface TemplateDetail {
  template: OnboardingTemplate;
  phases: OnboardingPhase[];
  milestones: OnboardingMilestone[];
  tasks: OnboardingTaskTemplate[];
}

export interface OnboardingPlan {
  id: number;
  cbo_id: number;
  customer_org_id: number;
  template_id: number | null;
  name: string | null;
  status: string;
  current_phase_id: number | null;
  current_phase_entered_at: string | null;
  start_date: string | null;
  target_go_live_date: string | null;
  actual_go_live_date: string | null;
  csm_user_id: number | null;
  health: string;
  health_recomputed_at: string | null;
  metadata: Record<string, unknown> | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  // joined fields
  customer_org_name?: string;
  template_name?: string;
  current_phase_name?: string;
  csm_user_name?: string;
  task_total?: number;
  task_done?: number;
  task_blocked?: number;
  task_overdue?: number;
}

export interface PlanTask {
  id: number;
  plan_id: number;
  task_template_id: number | null;
  phase_id: number | null;
  milestone_id: number | null;
  name: string;
  description: string | null;
  sort_order: number;
  action_type: string;
  action_target: string | null;
  action_payload: Record<string, unknown> | null;
  required: boolean;
  status: string;
  owner_user_id: number | null;
  owner_name?: string;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  blocker_reason: string | null;
  notes: string | null;
  links: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingNote {
  id: number;
  plan_id: number;
  author_id: number | null;
  author_name?: string;
  note_type: string;
  title: string | null;
  body: string;
  meeting_date: string | null;
  attendees: Array<Record<string, unknown>> | null;
  attachments: Array<Record<string, unknown>> | null;
  created_at: string;
  updated_at: string;
}

export interface OpenItem {
  id: number;
  plan_id: number;
  name: string;
  status: string;
  due_date: string | null;
  blocker_reason: string | null;
  owner_user_id: number | null;
  owner_name: string | null;
  customer_org_id: number;
  customer_org_name: string | null;
  csm_user_id: number | null;
  csm_name: string | null;
  phase_name: string | null;
  aging_days: number;
  reason: string;
}

export interface PortfolioData {
  by_status: Array<{ status: string; c: number }>;
  by_phase: Array<{ current_phase_id: number | null; phase_name: string | null; c: number; avg_days_in_phase: number | null }>;
  by_health: Array<{ health: string; c: number }>;
  by_csm: Array<{ csm_user_id: number | null; csm_name: string | null; c: number }>;
  upcoming_go_lives: Array<OnboardingPlan>;
  open_items_count: number;
}

/* -------------------------------------------------------------------------- */
/*  Templates                                                                  */
/* -------------------------------------------------------------------------- */

export async function listTemplates(params?: { is_active?: boolean; scope?: string }) {
  const res = await apiClient.get<{ templates: OnboardingTemplate[] }>(`${BASE}/templates`, { params });
  return res.data.templates;
}

export async function getTemplate(id: number) {
  const res = await apiClient.get<TemplateDetail>(`${BASE}/templates/${id}`);
  return res.data;
}

export async function createTemplate(data: { name: string; description?: string; customer_type?: string }) {
  const res = await apiClient.post<OnboardingTemplate>(`${BASE}/templates`, data);
  return res.data;
}

export async function updateTemplate(id: number, data: Partial<{ name: string; description: string; customer_type: string; is_active: boolean }>) {
  const res = await apiClient.patch(`${BASE}/templates/${id}`, data);
  return res.data;
}

export async function deleteTemplate(id: number) {
  return apiClient.delete(`${BASE}/templates/${id}`);
}

export async function cloneTemplate(id: number, newName?: string) {
  const res = await apiClient.post<{ id: number; name: string }>(`${BASE}/templates/${id}/clone`, null, { params: { new_name: newName } });
  return res.data;
}

// Phase / Milestone / Task template CRUD
export async function addPhase(templateId: number, data: { name: string; sort_order: number; target_duration_days?: number; description?: string }) {
  const res = await apiClient.post<OnboardingPhase>(`${BASE}/templates/${templateId}/phases`, data);
  return res.data;
}

export async function updatePhase(phaseId: number, data: { name: string; sort_order: number; target_duration_days?: number; description?: string }) {
  return apiClient.patch(`${BASE}/phases/${phaseId}`, data);
}

export async function deletePhase(phaseId: number) {
  return apiClient.delete(`${BASE}/phases/${phaseId}`);
}

export async function addMilestone(data: { phase_id: number; name: string; sort_order: number; exit_criteria?: string; description?: string }) {
  const res = await apiClient.post<OnboardingMilestone>(`${BASE}/milestones`, data);
  return res.data;
}

export async function updateMilestone(id: number, data: { phase_id: number; name: string; sort_order: number; exit_criteria?: string; description?: string }) {
  return apiClient.patch(`${BASE}/milestones/${id}`, data);
}

export async function deleteMilestone(id: number) {
  return apiClient.delete(`${BASE}/milestones/${id}`);
}

export async function addTaskTemplate(data: {
  milestone_id: number; name: string; sort_order: number; action_type?: string; action_target?: string;
  default_owner_role?: string; estimated_hours?: number; required?: boolean; description?: string;
}) {
  const res = await apiClient.post<OnboardingTaskTemplate>(`${BASE}/task-templates`, data);
  return res.data;
}

export async function updateTaskTemplate(id: number, data: Partial<OnboardingTaskTemplate>) {
  return apiClient.patch(`${BASE}/task-templates/${id}`, data);
}

export async function deleteTaskTemplate(id: number) {
  return apiClient.delete(`${BASE}/task-templates/${id}`);
}

/* -------------------------------------------------------------------------- */
/*  Plans                                                                      */
/* -------------------------------------------------------------------------- */

export async function listPlans(params?: { status?: string; csm_user_id?: number; customer_org_id?: number; health?: string }) {
  const res = await apiClient.get<{ plans: OnboardingPlan[] }>(`${BASE}/plans`, { params });
  return res.data.plans;
}

export async function createPlan(data: { customer_org_id: number; template_id: number; name?: string; start_date?: string; target_go_live_date?: string; csm_user_id?: number }) {
  const res = await apiClient.post<{ id: number }>(`${BASE}/plans`, data);
  return res.data;
}

export async function getPlan(id: number) {
  const res = await apiClient.get<{ plan: OnboardingPlan; phases: OnboardingPhase[]; tasks: PlanTask[] }>(`${BASE}/plans/${id}`);
  return res.data;
}

export async function updatePlan(id: number, data: Partial<{ name: string; status: string; csm_user_id: number; target_go_live_date: string; actual_go_live_date: string; metadata: Record<string, unknown> }>) {
  return apiClient.patch(`${BASE}/plans/${id}`, data);
}

export async function deletePlan(id: number) {
  return apiClient.delete(`${BASE}/plans/${id}`);
}

export async function updatePlanTask(planId: number, taskId: number, data: Partial<{ name: string; description: string; status: string; owner_user_id: number; due_date: string; blocker_reason: string; notes: string; action_target: string; required: boolean }>) {
  const res = await apiClient.patch<{ updated: number; derived: Record<string, unknown> }>(`${BASE}/plans/${planId}/tasks/${taskId}`, data);
  return res.data;
}

/* -------------------------------------------------------------------------- */
/*  Notes                                                                      */
/* -------------------------------------------------------------------------- */

export async function listNotes(planId: number) {
  const res = await apiClient.get<{ notes: OnboardingNote[] }>(`${BASE}/plans/${planId}/notes`);
  return res.data.notes;
}

export async function addNote(planId: number, data: { note_type: string; title?: string; body: string; meeting_date?: string; attendees?: Array<Record<string, unknown>> }) {
  const res = await apiClient.post<OnboardingNote>(`${BASE}/plans/${planId}/notes`, data);
  return res.data;
}

/* -------------------------------------------------------------------------- */
/*  Open Items & Portfolio                                                     */
/* -------------------------------------------------------------------------- */

export async function listOpenItems(params?: { csm_user_id?: number; customer_org_id?: number }) {
  const res = await apiClient.get<{ open_items: OpenItem[] }>(`${BASE}/open-items`, { params });
  return res.data.open_items;
}

export async function getPortfolio() {
  const res = await apiClient.get<PortfolioData>(`${BASE}/portfolio`);
  return res.data;
}

/* -------------------------------------------------------------------------- */
/*  Activities                                                                 */
/* -------------------------------------------------------------------------- */

export interface OnboardingActivity {
  id: number;
  // DB columns use 'type'/'title'/'completed' (not activity_type/subject/is_completed)
  type: string;
  activity_type?: string; // alias
  direction: string | null;
  title: string | null;
  subject?: string | null; // alias
  description: string | null;
  onboarding_task_id: number | null;
  task_name?: string | null;
  activity_date: string | null;
  due_date: string | null;
  completed: boolean;
  is_completed?: boolean; // alias
  completed_at: string | null;
  priority: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  user_id: number | null;
  created_by_name?: string | null;
  shared_with_org?: boolean;
  source?: string;
}

export async function listPlanActivities(planId: number, params?: { task_id?: number; activity_type?: string }) {
  const res = await apiClient.get<{ activities: OnboardingActivity[] }>(`${BASE}/plans/${planId}/activities`, { params });
  return res.data.activities;
}

export async function createPlanActivity(planId: number, data: {
  activity_type: string; subject: string; description?: string; task_id?: number;
  direction?: string; due_date?: string; is_completed?: boolean; priority?: string;
}) {
  const res = await apiClient.post<OnboardingActivity>(`${BASE}/plans/${planId}/activities`, data);
  return res.data;
}

export async function updatePlanActivity(planId: number, activityId: number, data: Partial<{ subject: string; description: string; is_completed: boolean; priority: string }>) {
  return apiClient.patch(`${BASE}/plans/${planId}/activities/${activityId}`, data);
}

export async function deletePlanActivity(planId: number, activityId: number) {
  return apiClient.delete(`${BASE}/plans/${planId}/activities/${activityId}`);
}

export async function listPresalesActivities(planId: number) {
  const res = await apiClient.get<{ activities: OnboardingActivity[] }>(`${BASE}/plans/${planId}/presales-activities`);
  return res.data.activities;
}

/* -------------------------------------------------------------------------- */
/*  Documents                                                                  */
/* -------------------------------------------------------------------------- */

export interface OnboardingDocument {
  id: string;
  plan_id: number;
  task_id: number | null;
  task_name?: string | null;
  document_type: string;
  document_name: string;
  description: string | null;
  file_url: string;
  file_size_bytes: number | null;
  file_mime_type: string | null;
  uploaded_by: number | null;
  uploaded_by_name?: string | null;
  uploaded_at: string;
  version: number;
  is_current: boolean;
  created_at: string;
}

export async function listPlanDocuments(planId: number, params?: { task_id?: number; document_type?: string }) {
  const res = await apiClient.get<{ documents: OnboardingDocument[] }>(`${BASE}/plans/${planId}/documents`, { params });
  return res.data.documents;
}

export async function createPlanDocument(planId: number, data: {
  document_type: string; document_name: string; description?: string;
  file_url: string; file_size_bytes?: number; file_mime_type?: string; task_id?: number;
}) {
  const res = await apiClient.post<OnboardingDocument>(`${BASE}/plans/${planId}/documents`, data);
  return res.data;
}

export async function updatePlanDocument(planId: number, docId: string, data: Partial<{
  document_type: string; document_name: string; description: string;
  file_url: string; task_id: number | null;
}>) {
  const res = await apiClient.put<OnboardingDocument>(`${BASE}/plans/${planId}/documents/${docId}`, data);
  return res.data;
}

export async function deletePlanDocument(planId: number, docId: string) {
  return apiClient.delete(`${BASE}/plans/${planId}/documents/${docId}`);
}

/* -------------------------------------------------------------------------- */
/*  Follow-ups                                                                 */
/* -------------------------------------------------------------------------- */

export async function createFollowup(planId: number, taskId: number, data: {
  name: string; description?: string; owner_user_id?: number; due_date?: string; priority?: string;
}) {
  const res = await apiClient.post(`${BASE}/plans/${planId}/tasks/${taskId}/followups`, data);
  return res.data;
}

export async function listFollowups(planId: number, taskId: number) {
  const res = await apiClient.get<{ followups: PlanTask[] }>(`${BASE}/plans/${planId}/tasks/${taskId}/followups`);
  return res.data.followups;
}
